import { useState, useEffect } from 'react';
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  Alignment,
  Autoformat,
  BlockQuote,
  Bold,
  ClassicEditor,
  Emoji,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  Indent,
  Italic,
  Link,
  List,
  ListProperties,
  Mention,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  Strikethrough,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  Underline
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";
import '../css/ticket.css';
import axiosInstance from "../../../axiosConfig";
import { AlertTriangle, ChevronDown, X } from 'lucide-react';
import FileInput from "../UI/InputFile";

class TicketDescriptionEditor extends ClassicEditor {}

TicketDescriptionEditor.builtinPlugins = [
  Essentials,
  Paragraph,
  Heading,
  Autoformat,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Link,
  List,
  ListProperties,
  BlockQuote,
  Alignment,
  Indent,
  FontFamily,
  FontSize,
  FontColor,
  FontBackgroundColor,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  PasteFromOffice,
  Mention,
  Emoji,
  RemoveFormat
];

TicketDescriptionEditor.defaultConfig = {
  licenseKey: "GPL",
  toolbar: {
    items: [
      "heading",
      "|",
      "fontFamily",
      "fontSize",
      "fontColor",
      "fontBackgroundColor",
      "|",
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "removeFormat",
      "|",
      "alignment",
      "|",
      "bulletedList",
      "numberedList",
      "outdent",
      "indent",
      "|",
      "link",
      "emoji",
      "blockQuote",
      "insertTable",
      "|",
      "undo",
      "redo"
    ],
    shouldNotGroupWhenFull: true
  },
  heading: {
    options: [
      { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
      { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
      { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
      { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" }
    ]
  },
  fontFamily: {
    supportAllValues: true
  },
  fontSize: {
    options: [10, 12, 14, "default", 18, 20, 22, 24, 28, 32],
    supportAllValues: true
  },
  fontColor: {
    columns: 8,
    documentColors: 10
  },
  fontBackgroundColor: {
    columns: 8,
    documentColors: 10
  },
  link: {
    addTargetToExternalLinks: true,
    defaultProtocol: "https://"
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true
    }
  },
  table: {
    contentToolbar: [
      "tableColumn",
      "tableRow",
      "mergeTableCells",
      "tableProperties",
      "tableCellProperties"
    ]
  }
};

export default function TicketForm({ ticketDetails, setTicketDetails, handleSubmit }) {
  const [metaData, setMetaData] = useState({
    ticketOrigins: [],
    ticketAgeGroups: [],
    paymentMethods: [],
    currencies: [],
    ticketTypes: [],
    ownershipTypes: [],
    qrFormats: []
  });
  const [loading, setLoading] = useState(true);

  // Helper functions - defined early to avoid initialization errors
  const getPricingCombinations = () => {
    const combos = [];
    const selectedOrigins = ticketDetails.ticketOrigins || [];
    const selectedAgeGroups = ticketDetails.ticketAgerId || [];

    if (!selectedOrigins.length || !selectedAgeGroups.length) return [];

    selectedOrigins.forEach((originId) => {
      const origin = metaData.ticketOrigins?.find((o) => String(o.id) === String(originId));
      selectedAgeGroups.forEach((ageGroupId) => {
        const age = metaData.ticketAgeGroups?.find((a) => String(a.id) === String(ageGroupId));
        if (origin && age) {
          combos.push({
            originId,
            originName: origin.name,
            ageGroupId,
            ageGroupName: age.name,
            key: `${originId}_${ageGroupId}`,
            tabLabel: `${origin.name} - ${age.name}`,
          });
        }
      });
    });

    return combos;
  };

  const pricingCombinations = getPricingCombinations();

  const buildEmptyPricingArray = () =>
    (pricingCombinations.length ? pricingCombinations : [{ key: "default", originId: null, ageGroupId: null }]).map(
      (c) => ({
        originId: c.originId,
        ageGroupId: c.ageGroupId,
        currencyCode: "LKR",
        isFreeTicket: false,
        basePrice: "",
        taxPercentage: "",
        discountMode: "Amount",
        discount: "",
        finalPrice: "",
        specialPrice: "",
        validFrom: "",
        validTill: "",
      })
    );
  const [showOriginsDropdown, setShowOriginsDropdown] = useState(false);
  const [showAgeGroupDropdown, setShowAgeGroupDropdown] = useState(false);
  const [destinationList, setDestinationList] = useState([]);
  const [destinationOwnerships, setDestinationOwnerships] = useState([]);
  const [isLoadingOwnerships, setIsLoadingOwnerships] = useState(false);
  const [availableTickets, setAvailableTickets] = useState([]);
  const [isLoadingAvailableTickets, setIsLoadingAvailableTickets] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showSessionAlert, setShowSessionAlert] = useState(false);
  const [showSessionBuilder, setShowSessionBuilder] = useState(false);
  const [bulkSession, setBulkSession] = useState({
    displayName: '',
    dayStartFrom: '',
    dayEndTo: '',
    validFrom: '',
    validTill: ''
  });
  const [bulkPricing, setBulkPricing] = useState(buildEmptyPricingArray());
  const [bulkActivePricingTab, setBulkActivePricingTab] = useState(0);
  const [bulkDays, setBulkDays] = useState([]);
  const itemsPerPage = 5;
  const currencyOptions = metaData.currencies?.length
    ? metaData.currencies
    : [{ id: "LKR", code: "LKR", name: "Sri Lankan Rupee" }];

  useEffect(() => {
  

    const fetchDestinationList = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          '/internal-api/v1/client-web/destination-panel/config/list',
          {
            params: {
              page: currentPage,
              size: itemsPerPage,
            }
          }
        );
        console.log("Destination List Response:", response.data.data);
        if (response.data?.data) {
          const seenIds = new Set();
          const uniqueDestinations = response.data.data.filter((item) => {
            if (!item || item.id === null || item.id === undefined) {
              return false;
            }
            if (seenIds.has(item.id)) {
              return false;
            }
            seenIds.add(item.id);
            return true;
          });
          setDestinationList(uniqueDestinations);
        }
      } catch (error) {
        console.error("Error fetching destinations:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDestinationList();
  }, [currentPage]);

  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        console.log("Fetching metadata...");
        const response = await axiosInstance.get(
          '/internal-api/v1/client-web/destination-panel/ticket/meta',
        );

        if (response.data && response.data.result) {
          setMetaData(response.data.result);
          console.log("ticketOrigins:", response.data.result.ticketOrigins);
          console.log("ticketAgeGroups:", response.data.result.ticketAgeGroups);
          console.log("paymentMethods:", response.data.result.paymentMethods);
        }
      } catch (error) {
        console.error("Error fetching metadata:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetaData();
  }, []);

  useEffect(() => {
    const nextTags = ticketDetails.tagCodes ? ticketDetails.tagCodes.join(', ') : '';
    if (nextTags !== tagInput) {
      setTagInput(nextTags);
    }
  }, [ticketDetails.tagCodes]);

  useEffect(() => {
    if (ticketDetails.sessions?.length) {
      setShowSessionAlert(false);
    }
  }, [ticketDetails.sessions?.length]);

  useEffect(() => {
    const fetchAvailableTickets = async () => {
      if (!ticketDetails.destinationId) {
        console.log("No destinationId selected, clearing available tickets");
        setAvailableTickets([]);
        if (ticketDetails.preTicketIds?.length) {
          setTicketDetails(prev => ({ ...prev, preTicketIds: [] }));
        }
        return;
      }

      try {
        setIsLoadingAvailableTickets(true);
        
        // Check if all age groups are selected - if so, send empty array (backend treats as "all")
        const allAgeGroupsSelected = metaData.ticketAgeGroups.length > 0 && 
          ticketDetails.ticketAgerId?.length === metaData.ticketAgeGroups.length;

        // Check if all origins are selected - if so, send empty array (backend treats as "all")
        const allOriginsSelected = metaData.ticketOrigins.length > 0 && 
          ticketDetails.ticketOrigins?.length === metaData.ticketOrigins.length;
        
        const requestPayload = {
          destinationIds: [Number(ticketDetails.destinationId)],
          ticketOriginsIds: allOriginsSelected ? [] : (ticketDetails.ticketOrigins || []),
          ticketAgerIds: allAgeGroupsSelected ? [] : (ticketDetails.ticketAgerId || []),
          ticketTypeIds: [],
          isGroupBtGroupCode: true
        };
        
        console.log("Fetching available tickets with payload:", requestPayload);
        console.log("All age groups selected:", allAgeGroupsSelected, `(${ticketDetails.ticketAgerId?.length}/${metaData.ticketAgeGroups.length})`);
        console.log("All origins selected:", allOriginsSelected, `(${ticketDetails.ticketOrigins?.length}/${metaData.ticketOrigins.length})`);
        
        const response = await axiosInstance.post(
          '/internal-api/v1/client-web/destination-panel/ticket/available-tickets',
          requestPayload
        );

        console.log("Available tickets API response:", response.data);

        const nextTickets = response.data?.result || response.data?.data || [];
        console.log("Extracted tickets array:", nextTickets);
        
        const normalizedTickets = Array.isArray(nextTickets) ? nextTickets : [];
        setAvailableTickets(normalizedTickets);
        console.log("Set available tickets count:", normalizedTickets.length);

        if (ticketDetails.preTicketIds?.length) {
          const allowedIds = new Set(normalizedTickets.map(ticket => String(ticket.id)));
          const filtered = ticketDetails.preTicketIds.filter(id => allowedIds.has(String(id)));
          if (filtered.length !== ticketDetails.preTicketIds.length) {
            setTicketDetails(prev => ({ ...prev, preTicketIds: filtered }));
          }
        }
      } catch (error) {
        console.error("Error fetching available tickets:", error);
        console.error("Error response:", error.response?.data);
        console.error("Error status:", error.response?.status);
        setAvailableTickets([]);
      } finally {
        setIsLoadingAvailableTickets(false);
      }
    };

    fetchAvailableTickets();
  }, [ticketDetails.destinationId, ticketDetails.ticketOrigins, ticketDetails.ticketAgerId, ticketDetails.ticketTypeId, setTicketDetails]);

  useEffect(() => {
    const fetchDestinationOwnerships = async () => {
      if (!ticketDetails.destinationId) {
        setDestinationOwnerships([]);
        if (ticketDetails.destinationOwnershipId) {
          setTicketDetails(prev => ({ ...prev, destinationOwnershipId: '' }));
        }
        return;
      }

      try {
        setIsLoadingOwnerships(true);
        const response = await axiosInstance.post(
          '/internal-api/v1/client-web/destination-panel/config/ownerships',
          {
            destinationIds: [Number(ticketDetails.destinationId)]
          }
        );

        const nextOwnerships = response.data?.result || response.data?.data || [];
        const normalized = Array.isArray(nextOwnerships) ? nextOwnerships : [];
        setDestinationOwnerships(normalized);

        if (ticketDetails.destinationOwnershipId) {
          const allowedIds = new Set(normalized.map(item => String(item.id)));
          if (!allowedIds.has(String(ticketDetails.destinationOwnershipId))) {
            setTicketDetails(prev => ({ ...prev, destinationOwnershipId: '' }));
          }
        }
      } catch (error) {
        console.error("Error fetching destination ownerships:", error);
        setDestinationOwnerships([]);
      } finally {
        setIsLoadingOwnerships(false);
      }
    };

    fetchDestinationOwnerships();
  }, [ticketDetails.destinationId, setTicketDetails]);

  useEffect(() => {
  setBulkPricing(buildEmptyPricingArray());
  setBulkActivePricingTab(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [ticketDetails.ticketOrigins, ticketDetails.ticketAgerId, metaData.ticketOrigins, metaData.ticketAgeGroups]);


useEffect(() => {
  if (!ticketDetails.sessions?.length) return;

  const selectedOrigins = ticketDetails.ticketOrigins || [];
  const selectedAgeGroups = ticketDetails.ticketAgerId || [];
  const waitingForPricingMeta =
    (selectedOrigins.length > 0 && !(metaData.ticketOrigins || []).length) ||
    (selectedAgeGroups.length > 0 && !(metaData.ticketAgeGroups || []).length);

  // Prevent wiping existing session pricing while metadata is still loading after remount.
  if (waitingForPricingMeta) return;

  setTicketDetails((prev) => ({
    ...prev,
    sessions: (prev.sessions || []).map((s) => {
      const current = Array.isArray(s.ticketPricing) ? s.ticketPricing : [];
      const expected = buildEmptyPricingArray();

      // Map current by key originId_ageGroupId
      const currentMap = new Map(
        current.map((p) => [`${p.originId}_${p.ageGroupId}`, p])
      );

      const merged = expected.map((p) => {
        const key = `${p.originId}_${p.ageGroupId}`;
        return currentMap.get(key) ? { ...p, ...currentMap.get(key) } : p;
      });

      return { ...s, ticketPricing: merged };
    }),
  }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [ticketDetails.ticketOrigins, ticketDetails.ticketAgerId, metaData.ticketOrigins, metaData.ticketAgeGroups]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setTicketDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDescriptionChange = (_, editor) => {
    const value = editor.getData();
    setTicketDetails(prev => ({
      ...prev,
      description: value
    }));
  };

  const createEmptyTicketImage = () => {
    const today = new Date().toISOString().split("T")[0];
    return {
      imageFile: null,
      displayOrder: "1",
      aspDivHeight: "100",
      aspDivWidth: "100",
      validFrom: today,
      validTo: today,
    };
  };

  const baseTicketImages = Array.isArray(ticketDetails.baseTicketImages)
    ? ticketDetails.baseTicketImages
    : [];

  const updateBaseTicketImages = (nextImages) => {
    setTicketDetails((prev) => ({
      ...prev,
      baseTicketImages: nextImages,
    }));
  };

  const handleBaseTicketImageChange = (imageIndex, field, value) => {
    const next = baseTicketImages.map((img, idx) =>
      idx === imageIndex ? { ...img, [field]: value } : img
    );
    updateBaseTicketImages(next);
  };

  const addBaseTicketImage = () => {
    updateBaseTicketImages([...baseTicketImages, createEmptyTicketImage()]);
  };

  const removeBaseTicketImage = (imageIndex) => {
    updateBaseTicketImages(baseTicketImages.filter((_, idx) => idx !== imageIndex));
  };

  // Handle checkbox changes for multiple selections
  const handleCheckboxChange = (fieldName, value) => {
    setTicketDetails(prev => {
      const currentValues = prev[fieldName] || [];
      const updatedValues = currentValues.includes(value)
        ? currentValues.filter(item => item !== value)
        : [...currentValues, value];

      return {
        ...prev,
        [fieldName]: updatedValues
      };
    });
  };

  // Updated to handle nested ticketValidity object
  const handleDurationChange = (e) => {
    const { name, value } = e.target;
    setTicketDetails(prev => ({
      ...prev,
      ticketValidity: {
        ...prev.ticketValidity,
        [name]: value
      }
    }));
  };

  const handleBulkPricingChange = (index, field, value) => {
  setBulkPricing((prev) =>
    prev.map((p, i) => {
      if (i !== index) return p;

      if (field === "isFreeTicket") {
        const isFreeTicket = value === true || value === 1 || value === "1";
        return {
          ...p,
          isFreeTicket,
          ...(isFreeTicket
            ? {
                basePrice: "0",
                taxPercentage: "0",
                discount: "0",
                finalPrice: "0.00",
                specialPrice: "0",
              }
            : {}),
        };
      }

      const updated = { ...p, [field]: value };
      updated.originId = p.originId;
      updated.ageGroupId = p.ageGroupId;

      const base = parseFloat(updated.basePrice) || 0;
      const tax = parseFloat(updated.taxPercentage) || 0;
      const disc = parseFloat(updated.discount) || 0;

      let final = base;
      if ((updated.discountMode || "Amount") === "Percentage") {
        final = base - base * (disc / 100);
      } else {
        final = base - disc;
      }
      final = final + final * (tax / 100);
      updated.finalPrice = Number.isFinite(final) ? final.toFixed(2) : "";

      return updated;
    })
  );
};


  // Updated session handler to match API field names
  const handleSessionChange = (id, field, value) => {
    setTicketDetails((prevDetails) => ({
      ...prevDetails,
      sessions: prevDetails.sessions.map((session) =>
        session.id === id ? { ...session, [field]: value } : session
      )
    }));
  };

const addNewSession = () => {
  const newId =
    ticketDetails.sessions.length > 0
      ? Math.max(...ticketDetails.sessions.map((s) => s.id)) + 1
      : 1;

  setTicketDetails((prev) => ({
    ...prev,
    sessions: [
      ...(prev.sessions || []),
      {
        id: newId,
        displayName: "",
        day: "",
        dayStartFrom: "",
        dayEndTo: "",
        displayOrder: newId,
        validFrom: "",
        validTill: "",
        ticketPricing: buildEmptyPricingArray(), // ✅ NEW
      },
    ],
  }));
};


  const removeSession = (id) => {
    setTicketDetails((prevDetails) => ({
      ...prevDetails,
      sessions: prevDetails.sessions.filter(session => session.id !== id)
    }));
  };

  const openSessionBuilder = () => {
    setShowSessionBuilder(true);
  };

  const toggleBulkDay = (day) => {
    setBulkDays((prev) => (
      prev.includes(day) ? prev.filter(item => item !== day) : [...prev, day]
    ));
  };
  

const addBulkSessions = () => {
  if (!bulkDays.length) return;

  const nextBaseId =
    ticketDetails.sessions.length > 0
      ? Math.max(...ticketDetails.sessions.map((s) => s.id)) + 1
      : 1;

  const sessionsToAdd = bulkDays.map((day, index) => ({
    id: nextBaseId + index,
    displayName: bulkSession.displayName,
    day,
    dayStartFrom: bulkSession.dayStartFrom,
    dayEndTo: bulkSession.dayEndTo,
    displayOrder: nextBaseId + index,
    validFrom: bulkSession.validFrom,
    validTill: bulkSession.validTill,

    // ✅ IMPORTANT: add pricing variants for THIS session
    ticketPricing: bulkPricing.map((p) => ({ ...p })),
  }));

  setTicketDetails((prevDetails) => ({
    ...prevDetails,
    sessions: [...(prevDetails.sessions || []), ...sessionsToAdd],
  }));

  // reset selection
  setBulkDays([]);
  setBulkSession({
    displayName: "",
    dayStartFrom: "",
    dayEndTo: "",
    validFrom: "",
    validTill: "",
  });
  setBulkPricing(buildEmptyPricingArray());
};


const handleSessionPricingChange = (sessionId, pricingIndex, field, value) => {
  setTicketDetails((prev) => ({
    ...prev,
    sessions: prev.sessions.map((s) => {
      if (s.id !== sessionId) return s;

      const nextPricing = (s.ticketPricing || []).map((p, idx) => {
        if (idx !== pricingIndex) return p;

        if (field === "isFreeTicket") {
          const isFreeTicket = value === true || value === 1 || value === "1";
          return {
            ...p,
            isFreeTicket,
            ...(isFreeTicket
              ? {
                  basePrice: "0",
                  taxPercentage: "0",
                  discount: "0",
                  finalPrice: "0.00",
                  specialPrice: "0",
                }
              : {}),
          };
        }

        const updated = { ...p, [field]: value };
        updated.originId = p.originId;
        updated.ageGroupId = p.ageGroupId;

        // auto-calc finalPrice
        const base = parseFloat(updated.basePrice) || 0;
        const tax = parseFloat(updated.taxPercentage) || 0;
        const disc = parseFloat(updated.discount) || 0;

        let final = base;

        if ((updated.discountMode || "Amount") === "Percentage") {
          final = base - base * (disc / 100);
        } else {
          final = base - disc;
        }

        final = final + final * (tax / 100);
        updated.finalPrice = Number.isFinite(final) ? final.toFixed(2) : "";

        return updated;
      });

      return { ...s, ticketPricing: nextPricing };
    }),
  }));
};


const copyPricingFromFirstSession = (targetSessionId) => {
  setTicketDetails((prev) => {
    const first = prev.sessions?.[0]?.ticketPricing;
    if (!Array.isArray(first)) return prev;

    return {
      ...prev,
      sessions: prev.sessions.map((s) =>
        s.id === targetSessionId ? { ...s, ticketPricing: first.map((p) => ({ ...p })) } : s
      ),
    };
  });
};

// const copyPricingToAllSessions = () => {
//   setTicketDetails((prev) => {
//     if (!prev.sessions || prev.sessions.length < 2) return prev;

//     const sourcePricing = prev.sessions[0]?.ticketPricing;
//     if (!Array.isArray(sourcePricing) || sourcePricing.length === 0) return prev;

//     return {
//       ...prev,
//       sessions: prev.sessions.map((session, index) => {
//         if (index === 0) return session; // keep first as-is

//         return {
//           ...session,
//           ticketPricing: sourcePricing.map((p) => ({ ...p })), // deep copy
//         };
//       }),
//     };
//   });
// };


  const handleFormSubmit = (e) => {
    if (!ticketDetails.sessions || ticketDetails.sessions.length === 0) {
      e.preventDefault();
      setShowSessionAlert(true);
      return;
    }
    setShowSessionAlert(false);
    handleSubmit(e);
  };

  return (
    <div className="ticket-form-container">
      <form onSubmit={handleFormSubmit} className="ticket-form">
        {/* Basic Information */}
        <div className="form-section">
          <h2 className="section-title">Basic Information</h2>
          <div className="grid-2">
            <div>
              <label className="block mb-1 text-xs font-medium text-slate-700">Destination <span className="text-red-500">*</span></label>
              <div className="relative">
                <select
                  name="destinationId"
                  value={ticketDetails.destinationId}
                  onChange={handleChange}
                  className="input-field"
                  required
                >
                  <option value="">Select destination</option>
                  {destinationList.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-slate-700">Destination Ownership<span className="text-red-500">*</span><span style={{ fontSize: '0.8em', color: '#dc2626' }} className='ml-2'>Ticket Issuing Entity</span></label>
              <div className="relative">
                <select
                  name="destinationOwnershipId"
                  value={ticketDetails.destinationOwnershipId || ''}
                  onChange={handleChange}
                  className="input-field"
                  required
                  disabled={!ticketDetails.destinationId || isLoadingOwnerships}
                >
                  <option value="">
                    {isLoadingOwnerships ? "Loading ownerships..." : "Select Ticket Ownership"}
                  </option>
                  {destinationOwnerships.map((ownership) => (
                    <option key={ownership.id} value={ownership.id}>
                      {ownership.ebName || ownership.eName || `Ownership ${ownership.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-slate-700">Ticket Type <span className="text-red-500">*</span></label>
              <div className="relative">
                <select
                  name="ticketTypeId"
                  value={ticketDetails.ticketTypeId}
                  onChange={handleChange}
                  className="input-field"
                  required
                >
                  <option value="">Select ticket type</option>
                  {metaData.ticketTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-slate-700">Ticket Revenue Share Type<span className="text-red-500">*</span></label>
              <div className="relative">
                <select
                  name="ownershipTypeCode"
                  value={ticketDetails.ownershipTypeCode}
                  onChange={handleChange}
                  className="input-field"
                  required
                >
                  <option value="">Ownership type</option>
                  {metaData.ownershipTypes.map((type) => (
                    <option key={type.code} value={type.code}>{type.name}</option>
                  ))}
                </select>
              </div>
            </div>  

            

            
          </div>
        </div>

        {/* Ticket Details */}
        <div className="form-section">
          <h2 className="section-title">Ticket Details</h2>
          <div className="mb-4 grid-2">
            <div>
              <label className="input-label required-field">Ticket Name</label>
              <input
                type="text"
                name="name"
                value={ticketDetails.name}
                onChange={handleChange}
                placeholder="Enter ticket name"
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="input-label required-field">Ticket Display Name</label>
              <input
                type="text"
                name="displayName"
                value={ticketDetails.displayName}
                onChange={handleChange}
                placeholder="Enter ticket display name"
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-slate-700">
                Ticket Age Group <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div
                  onClick={() => setShowAgeGroupDropdown(!showAgeGroupDropdown)}
                  className="w-full min-h-10 px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-md cursor-pointer flex items-center justify-between hover:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                >
                  <div className="flex flex-wrap flex-1 gap-1">
                    {ticketDetails.ticketAgerId?.length > 0 ? (
                      ticketDetails.ticketAgerId.map(id => {
                        const group = metaData.ticketAgeGroups.find(g => String(g.id) === String(id));
                        return (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-blue/10 text-brand-blue rounded-md"
                          >
                            {group?.name || id}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCheckboxChange('ticketAgerId', String(id));
                              }}
                              className="hover:bg-brand-blue/20 rounded-full p-0.5"
                            >
                              <X size={12} />
                            </button>
                          </span>
                        );
                      })
                    ) : (
                      <span className="text-gray-400">Select age groups...</span>
                    )}
                  </div>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${showAgeGroupDropdown ? 'rotate-180' : ''}`} />
                </div>

                {showAgeGroupDropdown && (
                  <div className="absolute left-0 right-0 z-10 mt-1 overflow-y-auto text-xs bg-white border rounded-md shadow top-full border-slate-300 max-h-48">
                    <div className="py-1">
                      {metaData.ticketAgeGroups.map(group => {
                        const isSelected = (ticketDetails.ticketAgerId || []).includes(String(group.id));
                        return (
                          <div
                            key={group.id}
                            onClick={() => handleCheckboxChange('ticketAgerId', String(group.id))}
                            className={`px-4 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-100 ${isSelected ? 'bg-brand-blue/5 text-brand-blue' : 'text-gray-700'
                              }`}
                          >
                            <span>{group.name}</span>
                            {isSelected && (
                              <div className="flex items-center justify-center w-3 h-3">
                                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-slate-700">
                Ticket Origin <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div
                  onClick={() => setShowOriginsDropdown(!showOriginsDropdown)}
                  className="w-full min-h-10 px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-md cursor-pointer flex items-center justify-between hover:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                >
                  <div className="flex flex-wrap flex-1 gap-1">
                    {ticketDetails.ticketOrigins?.length > 0 ? (
                      ticketDetails.ticketOrigins.map(code => {
                        const item = metaData.ticketOrigins.find(i => i.id === code);
                        return (
                          <span
                            key={code}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-blue/10 text-brand-blue rounded-md"
                          >
                            {item?.name || code}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCheckboxChange('ticketOrigins', code);
                              }}
                              className="hover:bg-brand-blue/20 rounded-full p-0.5"
                            >
                              <X size={12} />
                            </button>
                          </span>
                        );
                      })
                    ) : (
                      <span className="text-gray-400">Select ticket origins...</span>
                    )}
                  </div>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${showOriginsDropdown ? 'rotate-180' : ''}`} />
                </div>

                {showOriginsDropdown && (
                  <div className="absolute left-0 right-0 z-10 mt-1 overflow-y-auto text-xs bg-white border rounded-md shadow top-full border-slate-300 max-h-48">
                    <div className="py-1">
                      {metaData.ticketOrigins.map(option => {
                        const isSelected = (ticketDetails.ticketOrigins || []).includes(option.id);
                        return (
                          <div
                            key={option.code}
                            onClick={() => handleCheckboxChange('ticketOrigins', option.id)}
                            className={`px-4 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-100 ${isSelected ? 'bg-brand-blue/5 text-brand-blue' : 'text-gray-700'
                              }`}
                          >
                            <span>{option.name}</span>
                            {isSelected && (
                              <div className="flex items-center justify-center w-3 h-3 ">
                                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>

          <div className="mb-4">
            <label className="input-label">Short Description</label>
            <input
              type="text"
              name="shortDescription"
              value={ticketDetails.shortDescription}
              onChange={handleChange}
              placeholder="Brief description (max 255 characters)"
              className="input-field"
              maxLength={255}
            />
          </div>

          <div className="mb-4">
            <label className="input-label">Description</label>
            <div className="ticket-rich-text-editor">
              <CKEditor
                editor={TicketDescriptionEditor}
                data={ticketDetails.description || ""}
                config={{
                  placeholder: "Detailed description of the ticket"
                }}
                onChange={handleDescriptionChange}
              />
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-medium text-slate-700">Ticket / Package Images</h3>
                <p className="text-xs text-slate-500">
                  These images are submitted as base ticket images.
                </p>
              </div>
              <button
                type="button"
                onClick={addBaseTicketImage}
                className="px-4 py-2 text-xs text-white bg-brand-blue rounded hover:bg-brand-blue-dark"
              >
                Add Ticket / Package Image
              </button>
            </div>

            {baseTicketImages.length > 0 && (
              <div className="space-y-4">
                {baseTicketImages.map((image, idx) => (
                  <div key={idx} className="p-4 mb-4 rounded-md bg-slate-50">
                    <h4 className="mb-3 text-sm font-medium text-slate-700">
                      Base Image {idx + 1}
                    </h4>

                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block mb-1 text-xs font-medium text-slate-700">
                          Display Order
                        </label>
                        <input
                          type="number"
                          value={image.displayOrder}
                          onChange={(e) =>
                            handleBaseTicketImageChange(idx, "displayOrder", e.target.value)
                          }
                          min="1"
                          className="block w-full p-2 text-xs border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                        />
                      </div>

                      <div>
                        <label className="block mb-1 text-xs font-medium text-slate-700">
                          Valid From
                        </label>
                        <input
                          type="date"
                          value={image.validFrom}
                          onChange={(e) =>
                            handleBaseTicketImageChange(idx, "validFrom", e.target.value)
                          }
                          className="block w-full p-2 text-xs border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                        />
                      </div>

                      <div>
                        <label className="block mb-1 text-xs font-medium text-slate-700">
                          Valid To
                        </label>
                        <input
                          type="date"
                          value={image.validTo}
                          onChange={(e) =>
                            handleBaseTicketImageChange(idx, "validTo", e.target.value)
                          }
                          className="block w-full p-2 text-xs border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2">
                      <FileInput
                        value={ticketDetails.baseTicketImages?.[idx]?.imageFile}
                        onChange={(file) => handleBaseTicketImageChange(idx, "imageFile", file)}
                        label="Upload"
                        title="Web image"
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeBaseTicketImage(idx)}
                        className="px-4 py-2 text-xs text-white bg-red-500 rounded hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="">Ticket Available Period</label>
            <div style={{ fontSize: '0.8em', color: '#dc2626' }} className="mb-2">
              The time period during which the tickets are displayed in the portal
            </div>
            <div className="grid-2">
              <div>
                <label className="mb-1 input-label">From</label>
                <input
                  type="datetime-local"
                  name="validFrom"
                  value={ticketDetails.validFrom}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="mb-1 input-label">To</label>
                <input
                  type="datetime-local"
                  name="validTill"
                  value={ticketDetails.validTill}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className="">Ticket Active Period</label>
            <div style={{ fontSize: '0.8em', color: '#dc2626' }} className="mb-2">
              The exact time period during which the tickets can be used.
            </div>
            <div className="grid-2">
              <div>
                <label className="mb-1 input-label">From</label>
                <input
                  type="datetime-local"
                  name="registrationValidFrom"
                  value={ticketDetails.registrationValidFrom}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="mb-1 input-label">To</label>
                <input
                  type="datetime-local"
                  name="registrationValidTill"
                  value={ticketDetails.registrationValidTill}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className="input-label">Eligibility Criteria</label>
            <textarea
              name="eligibilityCriteria"
              value={ticketDetails.eligibilityCriteria}
              onChange={handleChange}
              placeholder="Detailed description of the eligibility criteria"
              className="input-field"
            ></textarea>
          </div>

          <div className="mb-4">
            <label className="input-label">Terms and Conditions</label>
            <textarea
              name="term"
              value={ticketDetails.term}
              onChange={handleChange}
              placeholder="Detailed description of the terms and conditions"
              className="input-field"
            ></textarea>
          </div>

            <div className="mb-4">
              <label className="input-label">Tags</label>
              <input  
                name="tags"
                placeholder="Enter tags separated by commas"
                className="input-field"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const tags = tagInput
                      .split(',')
                      .map(tag => tag.trim())
                      .filter(tag => tag.length > 0);
                    setTicketDetails(prev => ({
                      ...prev,
                      tagCodes: tags
                    }));
                    setTagInput(tags.join(', '));
                  }
                }}
                onBlur={() => {
                  const tags = tagInput
                    .split(',')
                    .map(tag => tag.trim())
                    .filter(tag => tag.length > 0);
                  setTicketDetails(prev => ({
                    ...prev,
                    tagCodes: tags
                  }));
                  setTagInput(tags.join(', '));
                }}
              />
            </div>

            <div>
              <label className="input-label">Payment Methods</label>
              <div className="relative">
                <select
                  name="paymentMethods"
                  value={ticketDetails.paymentMethods}
                  onChange={(e) => setTicketDetails({
                    ...ticketDetails,
                    paymentMethods: e.target.value ? [e.target.value] : []
                  })}
                  className="block w-full py-2 pl-3 pr-10 text-xs border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                >
                  <option value="">Select payment method</option>
                  {metaData.paymentMethods.map((type) => (
                    <option key={type.id} value={type.code}>{type.name}</option>
                  ))}
                </select>
              </div>
            </div>
          
        </div>

        
        {/* Additional Information */}
        <div className="form-section">
          <h2 className="section-title">Additional Information</h2>
          <div className="mb-4 grid-1">
            <div>
              <label className="block mb-1 text-xs font-medium text-slate-700">Pre-Request Tickets</label>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {(ticketDetails.preTicketIds || []).length === 0 && (
                    <span className="text-xs text-gray-400">Select pre-request tickets</span>
                  )}
                  {(ticketDetails.preTicketIds || []).map((id) => {
                    const ticket = availableTickets.find((item) => String(item.id) === String(id));
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-brand-blue bg-brand-blue/10 rounded"
                      >
                        {ticket?.name || id}
                        <button
                          type="button"
                          onClick={() => {
                            setTicketDetails(prev => ({
                              ...prev,
                              preTicketIds: prev.preTicketIds.filter(item => String(item) !== String(id))
                            }));
                          }}
                          className="text-brand-blue hover:text-brand-blue-dark"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
                <div className="p-2 overflow-y-auto text-xs bg-white border rounded-md max-h-32 border-slate-300">
                  {isLoadingAvailableTickets && (
                    <div className="py-2 text-gray-400">Loading tickets...</div>
                  )}
                  {!isLoadingAvailableTickets && availableTickets.length === 0 && (
                    <div className="py-2 text-gray-400">No tickets available</div>
                  )}
                  {!isLoadingAvailableTickets && availableTickets.map((ticket) => {
                    const checked = (ticketDetails.preTicketIds || []).some(
                      (id) => String(id) === String(ticket.id)
                    );
                    return (
                      <label key={ticket.id} className="flex items-center gap-2 py-1">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setTicketDetails(prev => {
                              const current = prev.preTicketIds || [];
                              const next = checked
                                ? current.filter(id => String(id) !== String(ticket.id))
                                : [...current, ticket.id];
                              return { ...prev, preTicketIds: next };
                            });
                          }}
                          disabled={!ticketDetails.destinationId}
                        />
                        <span className="text-gray-700">{ticket.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className='mb-2'>
              <label className="block mb-1 text-xs font-medium text-slate-700">QR Relese Type <span className="text-red-500">*</span></label>
              <div className="relative">
                <select
                  name="qrReleaseFormatCode"
                  value={ticketDetails.qrReleaseFormatCode}
                  onChange={handleChange}
                  className="input-field"
                  required
                >
                  <option value="">Select QR Type</option>
                  {metaData.qrFormats.map((type) => (
                    <option key={type.code} value={type.code}>{type.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid-2">
                      <div>
                        <label className="input-label">Is NIC Required?</label>
                        <div className="flex gap-4 mt-1">
                          <label className="flex items-center gap-1">
                            <input
                              type="radio"
                              name="isNicRequired"
                              value="1"
                              checked={ticketDetails.isNicRequired === '1'}
                              onChange={handleChange}
                            />
                            Yes
                          </label>
                          <label className="flex items-center gap-1">
                            <input
                              type="radio"
                              name="isNicRequired"
                              value="0"
                              checked={ticketDetails.isNicRequired === '0'}
                              onChange={handleChange}
                            />
                            No
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="input-label">Is Passport Required?</label>
                        <div className="flex gap-4 mt-1">
                          <label className="flex items-center gap-1">
                            <input
                              type="radio"
                              name="isPassportRequired"
                              value="1"
                              checked={ticketDetails.isPassportRequired === '1'}
                              onChange={handleChange}
                            />
                            Yes
                          </label>
                          <label className="flex items-center gap-1">
                            <input
                              type="radio"
                              name="isPassportRequired"
                              value="0"
                              checked={ticketDetails.isPassportRequired === '0'}
                              onChange={handleChange}
                            />
                            No
                          </label>
                        </div>
                      </div>
                    </div>
       
          </div>

          

          {/* <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-1 text-xs font-medium text-slate-700">
                Terms & Conditions (PDF)
              </label>
              <div className="flex items-center">
                <span className="block w-full p-2 text-xs border rounded-md border-slate-300 bg-gray-50">
                  {ticketDetails?.pdfFile ? ticketDetails?.pdfFile?.name : 'No file chosen'}
                </span>

                <label className="px-4 py-2 ml-2 text-xs font-medium text-white bg-brand-blue rounded cursor-pointer hover:bg-brand-blue-dark">
                  Browse
                  <input type="file" accept=".pdf"
                    onChange={(e) => setTicketDetails({
                      ...ticketDetails,
                      pdfFile: e.target.files[0]
                    })}
                    className="hidden" />
                </label>
              </div>
            </div>
          </div> */}
          
        </div>
        
        
            
        {/* Ticket Validity */}
        <div className="form-section">
          <h2 className="section-title">Ticket Validity After Purchase</h2>
          <div className="mb-4 grid-2">
            <div>
              <label className="mb-1 input-label">Valid From</label>
              <input
                type="datetime-local"
                name="validFrom"
                value={ticketDetails.ticketValidity.validFrom}
               onChange={(e) => setTicketDetails({
                  ...ticketDetails,
                  ticketValidity: {
                    ...ticketDetails.ticketValidity,
                    validFrom: e.target.value
                  }
                })}
                className="input-field"
              />
            </div>
            <div>
              <label className="mb-1 input-label">Valid To</label>
              <input
                type="datetime-local"
                name="validTill"
                value={ticketDetails.ticketValidity.validTill}
                onChange={(e) => setTicketDetails({
                  ...ticketDetails,
                  ticketValidity: {
                    ...ticketDetails.ticketValidity,
                    validTill: e.target.value
                  }
                })}
                className="input-field"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 input-label">Or, select duration</label>
            <div className="grid-3">
              <div>
                <label className="mb-1 input-label">Days:</label>
                <input
                  type="number"
                  name="noOfDays"
                  value={ticketDetails.ticketValidity.noOfDays}
                  onChange={handleDurationChange}
                  min="0"
                  className="input-field"
                />
              </div>
              <div>
                <label className="mb-1 input-label">Months:</label>
                <input
                  type="number"
                  name="noOfMonth"
                  value={ticketDetails.ticketValidity.noOfMonth}
                  onChange={handleDurationChange}
                  min="0"
                  className="input-field"
                />
              </div>
              <div>
                <label className="mb-1 input-label">Years:</label>
                <input
                  type="number"
                  name="noOfYears"
                  value={ticketDetails.ticketValidity.noOfYears}
                  onChange={handleDurationChange}
                  min="0"
                  className="input-field"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Ticket Sessions */}
<div className="p-6 bg-white border rounded-xl border-slate-200">
  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
  <h2 className="text-base font-medium text-slate-700">Ticket Sessions</h2>

  <div className="flex gap-2">
    <button
      type="button"
      onClick={() => setShowSessionBuilder((p) => !p)}
      className="px-4 py-2 text-xs bg-white border rounded border-slate-200 hover:bg-slate-50"
      disabled={!pricingCombinations.length}
      title={!pricingCombinations.length ? "Select Ticket Origin + Age Group first" : ""}
    >
      {showSessionBuilder ? "Hide Bulk Builder" : "Bulk Add Sessions"}
    </button>

    <button
      type="button"
      onClick={addNewSession}
      className="px-4 py-2 text-xs text-white bg-brand-blue rounded hover:bg-brand-blue-dark"
      disabled={!pricingCombinations.length}
      title={!pricingCombinations.length ? "Select Ticket Origin + Age Group first" : ""}
    >
      + Add Session
    </button>
  </div>
</div>

{showSessionBuilder && (
  <div className="p-4 mb-5 border rounded-lg border-slate-200 bg-slate-50">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Bulk add sessions</h3>
        <p className="text-xs text-slate-500">
          Add same session details for multiple days. Pricing variants will be created automatically.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
  {/* <button
    type="button"
    onClick={copyPricingToAllSessions}
    disabled={(ticketDetails.sessions || []).length < 2}
    className="px-3 py-2 text-xs text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
    title="Copies pricing from Session 1 to all sessions"
  >
    Copy pricing to ALL sessions
  </button> */}

  <button
    type="button"
    onClick={() => {
      setBulkDays([]);
      setBulkSession({
        displayName: "",
        dayStartFrom: "",
        dayEndTo: "",
        validFrom: "",
        validTill: "",
      });
    }}
    className="px-3 py-2 text-xs bg-white border rounded border-slate-200 hover:bg-slate-100"
  >
    Clear
  </button>

  <button
    type="button"
    onClick={addBulkSessions}
    disabled={!bulkDays.length}
    className="px-3 py-2 text-xs text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
  >
    Add for selected days
  </button>
</div>
    </div>

    <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2 lg:grid-cols-3">
      <div>
        <label className="mb-1 input-label">Session Name</label>
        <input
          type="text"
          value={bulkSession.displayName}
          onChange={(e) => setBulkSession((p) => ({ ...p, displayName: e.target.value }))}
          className="input-field"
          placeholder="E.g. Morning Session"
        />
      </div>

      <div>
        <label className="mb-1 input-label">Start</label>
        <input
          type="time"
          value={bulkSession.dayStartFrom}
          onChange={(e) => setBulkSession((p) => ({ ...p, dayStartFrom: e.target.value }))}
          className="input-field"
        />
      </div>

      <div>
        <label className="mb-1 input-label">End</label>
        <input
          type="time"
          value={bulkSession.dayEndTo}
          onChange={(e) => setBulkSession((p) => ({ ...p, dayEndTo: e.target.value }))}
          className="input-field"
        />
      </div>

      <div>
        <label className="mb-1 input-label">Valid From</label>
        <input
          type="datetime-local"
          value={bulkSession.validFrom}
          onChange={(e) => setBulkSession((p) => ({ ...p, validFrom: e.target.value }))}
          className="input-field"
        />
      </div>

      <div>
        <label className="mb-1 input-label">Valid To</label>
        <input
          type="datetime-local"
          value={bulkSession.validTill}
          onChange={(e) => setBulkSession((p) => ({ ...p, validTill: e.target.value }))}
          className="input-field"
        />
      </div>
    </div>

    {/* Day pickers */}
    <div className="mt-4">
      <label className="mb-2 input-label">Days</label>

      <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-4 text-[11px]">
        <button
          type="button"
          onClick={() => setBulkDays(["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"])}
          className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600 hover:bg-slate-100"
        >
          Select all
        </button>
        <button
          type="button"
          onClick={() => setBulkDays(["Monday","Tuesday","Wednesday","Thursday","Friday"])}
          className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600 hover:bg-slate-100"
        >
          Weekdays
        </button>
        <button
          type="button"
          onClick={() => setBulkDays(["Saturday","Sunday"])}
          className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600 hover:bg-slate-100"
        >
          Weekend
        </button>
        <button
          type="button"
          onClick={() => setBulkDays([])}
          className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600 hover:bg-slate-100"
        >
          Clear days
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
        {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((day) => {
          const selected = bulkDays.includes(day);
          return (
            <button
              key={day}
              type="button"
              onClick={() => toggleBulkDay(day)}
              className={`rounded-lg border px-2 py-1.5 text-xs font-semibold tracking-wide transition ${
                selected
                  ? "border-brand-blue bg-brand-blue text-white shadow-sm"
                  : "border-slate-200 bg-white text-slate-600 hover:border-brand-blue/20 hover:text-brand-blue"
              }`}
              title={day}
            >
              {day.slice(0, 3)}
            </button>
          );
        })}
      </div>
    </div>

    {/* ✅ Bulk pricing section */}
<div className="p-4 mt-4 bg-white border rounded-md border-slate-200">
  <div className="mb-2 text-xs font-semibold text-slate-700">
    Pricing for Bulk Sessions (per variant)
  </div>

  <div className="flex flex-wrap mb-4 overflow-x-auto border-b border-slate-200">
    {(pricingCombinations.length ? pricingCombinations : [{ key: "default", tabLabel: "Default" }]).map((v, idx) => (
      <button
        key={v.key || idx}
        type="button"
        onClick={() => setBulkActivePricingTab(idx)}
        className={`px-3 py-2 text-xs font-medium border-b-2 mr-2 mb-2 whitespace-nowrap transition-colors ${
          bulkActivePricingTab === idx
            ? "border-brand-blue text-brand-blue bg-brand-blue/5"
            : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
        }`}
      >
        {v.tabLabel}
      </button>
    ))}
  </div>

  {bulkPricing[bulkActivePricingTab] && (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <label className="flex items-center gap-3 p-3 border rounded-md cursor-pointer border-slate-200 bg-slate-50 md:col-span-3">
        <input
          type="checkbox"
          checked={Boolean(bulkPricing[bulkActivePricingTab].isFreeTicket)}
          onChange={(e) => handleBulkPricingChange(bulkActivePricingTab, "isFreeTicket", e.target.checked)}
          className="w-4 h-4 text-brand-blue border-slate-300 rounded"
        />
        <span>
          <span className="block text-xs font-semibold text-slate-700">Free ticket</span>
          <span className="block text-[11px] text-slate-500">Marks this pricing option as free and sets its price values to zero.</span>
        </span>
      </label>

      <div>
        <label className="block mb-1 text-xs font-medium text-slate-700">Currency</label>
        <select
          value={bulkPricing[bulkActivePricingTab].currencyCode || "LKR"}
          onChange={(e) => handleBulkPricingChange(bulkActivePricingTab, "currencyCode", e.target.value)}
          className="block w-full p-2 text-xs border rounded-md border-slate-300"
        >
          {currencyOptions.map((currency) => (
            <option key={currency.id ?? currency.code} value={currency.code}>
              {currency.name} ({currency.code})
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block mb-1 text-xs font-medium text-slate-700">Base Price</label>
        <input
          type="number"
          value={bulkPricing[bulkActivePricingTab].basePrice || ""}
          onChange={(e) => handleBulkPricingChange(bulkActivePricingTab, "basePrice", e.target.value)}
          disabled={Boolean(bulkPricing[bulkActivePricingTab].isFreeTicket)}
          className="block w-full p-2 text-xs border rounded-md border-slate-300 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
      </div>

      <div>
        <label className="block mb-1 text-xs font-medium text-slate-700">Tax %</label>
        <input
          type="number"
          value={bulkPricing[bulkActivePricingTab].taxPercentage || ""}
          onChange={(e) => handleBulkPricingChange(bulkActivePricingTab, "taxPercentage", e.target.value)}
          disabled={Boolean(bulkPricing[bulkActivePricingTab].isFreeTicket)}
          className="block w-full p-2 text-xs border rounded-md border-slate-300 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
      </div>

      <div>
        <label className="block mb-1 text-xs font-medium text-slate-700">Discount Mode</label>
        <select
          value={bulkPricing[bulkActivePricingTab].discountMode || "Amount"}
          onChange={(e) => handleBulkPricingChange(bulkActivePricingTab, "discountMode", e.target.value)}
          disabled={Boolean(bulkPricing[bulkActivePricingTab].isFreeTicket)}
          className="block w-full p-2 text-xs border rounded-md border-slate-300 disabled:cursor-not-allowed disabled:bg-slate-100"
        >
          <option value="Amount">Amount</option>
          <option value="Percentage">Percentage</option>
        </select>
      </div>

      <div>
        <label className="block mb-1 text-xs font-medium text-slate-700">Discount</label>
        <input
          type="number"
          value={bulkPricing[bulkActivePricingTab].discount || ""}
          onChange={(e) => handleBulkPricingChange(bulkActivePricingTab, "discount", e.target.value)}
          disabled={Boolean(bulkPricing[bulkActivePricingTab].isFreeTicket)}
          className="block w-full p-2 text-xs border rounded-md border-slate-300 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
      </div>

      <div>
        <label className="block mb-1 text-xs font-medium text-slate-700">Final Price</label>
        <input
          type="number"
          value={bulkPricing[bulkActivePricingTab].finalPrice || ""}
          readOnly
          className="block w-full p-2 text-xs border rounded-md border-slate-300 bg-slate-50"
        />
      </div>

      <div>
        <label className="block mb-1 text-xs font-medium text-slate-700">Special Price</label>
        <input
          type="number"
          value={bulkPricing[bulkActivePricingTab].specialPrice || ""}
          onChange={(e) => handleBulkPricingChange(bulkActivePricingTab, "specialPrice", e.target.value)}
          disabled={Boolean(bulkPricing[bulkActivePricingTab].isFreeTicket)}
          className="block w-full p-2 text-xs border rounded-md border-slate-300 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
      </div>

      <div>
        <label className="block mb-1 text-xs font-medium text-slate-700">Price Valid From</label>
        <input
          type="date"
          value={bulkPricing[bulkActivePricingTab].validFrom || ""}
          onChange={(e) => handleBulkPricingChange(bulkActivePricingTab, "validFrom", e.target.value)}
          className="block w-full p-2 text-xs border rounded-md border-slate-300"
        />
      </div>

      <div>
        <label className="block mb-1 text-xs font-medium text-slate-700">Price Valid Till</label>
        <input
          type="date"
          value={bulkPricing[bulkActivePricingTab].validTill || ""}
          onChange={(e) => handleBulkPricingChange(bulkActivePricingTab, "validTill", e.target.value)}
          className="block w-full p-2 text-xs border rounded-md border-slate-300"
        />
      </div>
    </div>
  )}
</div>
    
  </div>
)}


  {!pricingCombinations.length && (
    <div className="p-3 mb-4 text-xs text-yellow-800 border border-yellow-200 rounded-md bg-yellow-50">
      Please select <b>Ticket Origin</b> and <b>Ticket Age Group</b> first. Session pricing variants depend on them.
    </div>
  )}

  {showSessionAlert && (
    <div className="px-4 py-3 mb-4 border rounded-lg shadow-sm border-rose-200 bg-rose-50 text-rose-800">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-rose-100">
          <AlertTriangle size={18} className="text-rose-600" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold">Add at least one ticket session</p>
          <p className="text-xs text-rose-700">Please add a session before continuing.</p>
        </div>
        <button
          type="button"
          onClick={() => setShowSessionAlert(false)}
          className="p-1 rounded-md text-rose-700 hover:bg-rose-100"
          aria-label="Dismiss session alert"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  )}

  {(ticketDetails.sessions || []).map((session, sIdx) => (
    <SessionCard
      key={session.id}
      session={session}
      pricingCombinations={pricingCombinations}
      currencies={metaData.currencies}
      onSessionChange={handleSessionChange}
      onRemove={removeSession}
      onPricingChange={handleSessionPricingChange}
      onCopyFromFirst={() => copyPricingFromFirstSession(session.id)}
      allowCopy={sIdx !== 0}
    />
  ))}

  {(ticketDetails.sessions || []).length === 0 && (
    <div className="py-6 text-xs text-center text-slate-500">
      No sessions added yet.
    </div>
  )}
</div>


        {/* Submit Button */}
        <div className="flex-end">
          <button
            type="submit"
            className="button button-primary"
          >
            Next : Ownership & Images
          </button>
        </div>
      </form>
    </div>
  );
}

function SessionCard({
  session,
  pricingCombinations,
  currencies = [],
  onSessionChange,
  onPricingChange,
  onRemove,
  onCopyFromFirst,
  allowCopy
}) {
  const [activeVariantTab, setActiveVariantTab] = useState(0);
  const variants = pricingCombinations.length ? pricingCombinations : [{ tabLabel: "Default", key: "default" }];
  const currentPricing = session.ticketPricing?.[activeVariantTab] || {};
  const currencyOptions = currencies.length
    ? currencies
    : [{ id: "LKR", code: "LKR", name: "Sri Lankan Rupee" }];

  return (
    <div className="p-4 mb-4 rounded-md bg-slate-50">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-slate-700">Session {session.id}</h3>

        <div className="flex gap-2">
          {allowCopy && (
            <button
              type="button"
              onClick={onCopyFromFirst}
              className="px-3 py-1 text-[11px] bg-white border rounded border-slate-200 hover:bg-slate-100"
            >
              Copy pricing from Session 1
            </button>
          )}

          <button
            type="button"
            onClick={() => onRemove(session.id)}
            className="px-3 py-1 text-[11px] text-white bg-red-500 rounded hover:bg-red-600"
          >
            Remove
          </button>
        </div>
      </div>

      {/* Session basic fields */}
      <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2">
        <div>
          <label className="mb-1 input-label">Session Name</label>
          <input
            type="text"
            value={session.displayName}
            onChange={(e) => onSessionChange(session.id, "displayName", e.target.value)}
            className="input-field"
          />
        </div>

        <div>
          <label className="block mb-1 text-xs font-medium text-slate-700">Day</label>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
            {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((day) => {
              const selected = session.day === day;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => onSessionChange(session.id, "day", day)}
                  className={`rounded-lg border px-2 py-1 text-[11px] font-semibold tracking-wide transition ${
                    selected
                      ? "border-brand-blue bg-brand-blue text-white shadow-sm"
                      : "border-slate-200 bg-white text-slate-600 hover:border-brand-blue/20 hover:text-brand-blue"
                  }`}
                >
                  {day.slice(0,3)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 md:grid-cols-3">
        <div>
          <label className="mb-1 input-label">Session Start</label>
          <input
            type="time"
            value={session.dayStartFrom}
            onChange={(e) => onSessionChange(session.id, "dayStartFrom", e.target.value)}
            className="input-field"
          />
        </div>

        <div>
          <label className="mb-1 input-label">Session End</label>
          <input
            type="time"
            value={session.dayEndTo}
            onChange={(e) => onSessionChange(session.id, "dayEndTo", e.target.value)}
            className="input-field"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 mb-5 md:grid-cols-2">
        <div>
          <label className="mb-1 input-label">Valid From</label>
          <input
            type="datetime-local"
            value={session.validFrom}
            onChange={(e) => onSessionChange(session.id, "validFrom", e.target.value)}
            className="input-field"
          />
        </div>
        <div>
          <label className="mb-1 input-label">Valid To</label>
          <input
            type="datetime-local"
            value={session.validTill}
            onChange={(e) => onSessionChange(session.id, "validTill", e.target.value)}
            className="input-field"
          />
        </div>
      </div>

      {/* ✅ NEW: Session Variant Pricing */}
      <div className="p-4 bg-white border rounded-md border-slate-200">
        <div className="mb-3 text-xs font-semibold text-slate-700">
          Pricing for this Session (per variant)
        </div>

        <div className="flex flex-wrap mb-4 overflow-x-auto border-b border-slate-200">
          {variants.map((v, idx) => (
            <button
              key={v.key || idx}
              type="button"
              onClick={() => setActiveVariantTab(idx)}
              className={`px-3 py-2 text-xs font-medium border-b-2 mr-2 mb-2 whitespace-nowrap transition-colors ${
                activeVariantTab === idx
                  ? "border-brand-blue text-brand-blue bg-brand-blue/5"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
            >
              {v.tabLabel}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <label className="flex items-center gap-3 p-3 border rounded-md cursor-pointer border-slate-200 bg-slate-50 md:col-span-3">
            <input
              type="checkbox"
              checked={Boolean(currentPricing.isFreeTicket)}
              onChange={(e) => onPricingChange(session.id, activeVariantTab, "isFreeTicket", e.target.checked)}
              className="w-4 h-4 text-brand-blue border-slate-300 rounded"
            />
            <span>
              <span className="block text-xs font-semibold text-slate-700">Free ticket</span>
              <span className="block text-[11px] text-slate-500">Marks this pricing option as free and sets its price values to zero.</span>
            </span>
          </label>

          <div>
            <label className="block mb-1 text-xs font-medium text-slate-700">Currency</label>
            <select
              value={currentPricing.currencyCode || "LKR"}
              onChange={(e) => onPricingChange(session.id, activeVariantTab, "currencyCode", e.target.value)}
              className="block w-full p-2 text-xs border rounded-md border-slate-300"
            >
              {currencyOptions.map((currency) => (
                <option key={currency.id ?? currency.code} value={currency.code}>
                  {currency.name} ({currency.code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium text-slate-700">Base Price</label>
            <input
              type="number"
              value={currentPricing.basePrice || ""}
              onChange={(e) => onPricingChange(session.id, activeVariantTab, "basePrice", e.target.value)}
              disabled={Boolean(currentPricing.isFreeTicket)}
              className="block w-full p-2 text-xs border rounded-md border-slate-300 disabled:cursor-not-allowed disabled:bg-slate-100"
            />
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium text-slate-700">Tax %</label>
            <input
              type="number"
              value={currentPricing.taxPercentage || ""}
              onChange={(e) => onPricingChange(session.id, activeVariantTab, "taxPercentage", e.target.value)}
              disabled={Boolean(currentPricing.isFreeTicket)}
              className="block w-full p-2 text-xs border rounded-md border-slate-300 disabled:cursor-not-allowed disabled:bg-slate-100"
            />
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium text-slate-700">Discount Mode</label>
            <select
              value={currentPricing.discountMode || "Amount"}
              onChange={(e) => onPricingChange(session.id, activeVariantTab, "discountMode", e.target.value)}
              disabled={Boolean(currentPricing.isFreeTicket)}
              className="block w-full p-2 text-xs border rounded-md border-slate-300 disabled:cursor-not-allowed disabled:bg-slate-100"
            >
              <option value="Amount">Amount</option>
              <option value="Percentage">Percentage</option>
            </select>
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium text-slate-700">Discount</label>
            <input
              type="number"
              value={currentPricing.discount || ""}
              onChange={(e) => onPricingChange(session.id, activeVariantTab, "discount", e.target.value)}
              disabled={Boolean(currentPricing.isFreeTicket)}
              className="block w-full p-2 text-xs border rounded-md border-slate-300 disabled:cursor-not-allowed disabled:bg-slate-100"
            />
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium text-slate-700">Final Price</label>
            <input
              type="number"
              value={currentPricing.finalPrice || ""}
              readOnly
              className="block w-full p-2 text-xs border rounded-md border-slate-300 bg-slate-50"
            />
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium text-slate-700">Special Price</label>
            <input
              type="number"
              value={currentPricing.specialPrice || ""}
              onChange={(e) => onPricingChange(session.id, activeVariantTab, "specialPrice", e.target.value)}
              disabled={Boolean(currentPricing.isFreeTicket)}
              className="block w-full p-2 text-xs border rounded-md border-slate-300 disabled:cursor-not-allowed disabled:bg-slate-100"
            />
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium text-slate-700">Price Valid From</label>
            <input
              type="date"
              value={currentPricing.validFrom || ""}
              onChange={(e) => onPricingChange(session.id, activeVariantTab, "validFrom", e.target.value)}
              className="block w-full p-2 text-xs border rounded-md border-slate-300"
            />
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium text-slate-700">Price Valid Till</label>
            <input
              type="date"
              value={currentPricing.validTill || ""}
              onChange={(e) => onPricingChange(session.id, activeVariantTab, "validTill", e.target.value)}
              className="block w-full p-2 text-xs border rounded-md border-slate-300"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

